import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Truck, Package, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

// Carrier tracking URL patterns. {n} is replaced with the tracking number.
const CARRIER_URL = {
  USPS: "https://tools.usps.com/go/TrackConfirmAction?tLabels={n}",
  FedEx: "https://www.fedex.com/fedextrack/?trknbr={n}",
  UPS: "https://www.ups.com/track?tracknum={n}",
  DHL: "https://www.dhl.com/en/express/tracking.html?AWB={n}",
  Other: "",
};

function buildTrackingUrl(carrier, number) {
  const tpl = CARRIER_URL[carrier];
  if (!tpl || !number) return "";
  return tpl.replace("{n}", encodeURIComponent(number.trim()));
}

function StatusPill({ status }) {
  if (status === "paid") return <span className="text-[10px] uppercase tracking-widest text-[#FFB800] border border-[#FFB800]/30 px-2 py-1">To ship</span>;
  if (status === "shipped") return <span className="text-[10px] uppercase tracking-widest text-[#05D9E8] border border-[#05D9E8]/30 px-2 py-1">Shipped</span>;
  if (status === "delivered") return <span className="text-[10px] uppercase tracking-widest text-[#CCFF00] border border-[#CCFF00]/30 px-2 py-1">Delivered</span>;
  return <span className="text-[10px] uppercase tracking-widest text-zinc-400 border border-white/10 px-2 py-1">{status}</span>;
}

function ShipForm({ orderId, onDone }) {
  const [open, setOpen] = useState(false);
  const [carrier, setCarrier] = useState("USPS");
  const [number, setNumber] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const urlTouched = useRef(false);

  // Auto-fill URL from carrier + number unless user has manually edited it
  useEffect(() => {
    if (urlTouched.current) return;
    setUrl(buildTrackingUrl(carrier, number));
  }, [carrier, number]);

  const onUrlChange = (e) => {
    urlTouched.current = true;
    setUrl(e.target.value);
  };

  const resetUrlOverride = () => {
    urlTouched.current = false;
    setUrl(buildTrackingUrl(carrier, number));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!number) return;
    setBusy(true);
    try {
      await api.post(`/seller/orders/${orderId}/ship`, { carrier, number, url: url || null });
      toast.success("Order marked as shipped");
      setOpen(false); onDone?.();
    } catch { toast.error("Could not update order"); }
    finally { setBusy(false); }
  };

  if (!open) return <button data-testid={`open-ship-${orderId}`} onClick={() => setOpen(true)} className="btn-primary !py-2 !px-3 !text-[10px] inline-flex items-center gap-1"><Truck className="w-3 h-3" /> Ship</button>;

  return (
    <form onSubmit={submit} className="flex flex-wrap items-center gap-2" data-testid={`ship-form-${orderId}`}>
      <select value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid={`ship-carrier-${orderId}`} className="input-dark !py-1 !px-2 text-xs">
        {Object.keys(CARRIER_URL).map(c => <option key={c} value={c}>{c}</option>)}
      </select>
      <input data-testid={`ship-number-${orderId}`} placeholder="Tracking #" value={number} onChange={(e) => setNumber(e.target.value)} required className="input-dark !py-1 !px-2 text-xs" />
      <input
        data-testid={`ship-url-${orderId}`}
        placeholder={carrier === "Other" ? "Tracking URL (optional)" : "Auto-filled — edit to override"}
        value={url}
        onChange={onUrlChange}
        className="input-dark !py-1 !px-2 text-xs w-56"
      />
      {urlTouched.current && carrier !== "Other" && (
        <button type="button" onClick={resetUrlOverride} data-testid={`ship-url-reset-${orderId}`} className="text-[10px] uppercase tracking-widest text-[#05D9E8] hover:underline">Auto</button>
      )}
      <button data-testid={`ship-submit-${orderId}`} disabled={busy} className="btn-primary !py-1 !px-3 !text-[10px]">{busy ? "…" : "Save"}</button>
      <button type="button" onClick={() => setOpen(false)} className="text-xs text-zinc-500 hover:text-white">Cancel</button>
    </form>
  );
}

export default function SellerOrders() {
  const [orders, setOrders] = useState(null);

  const load = async () => {
    const { data } = await api.get("/seller/orders");
    setOrders(data || []);
  };
  useEffect(() => { load(); }, []);

  const markDelivered = async (id) => {
    try {
      await api.post(`/seller/orders/${id}/deliver`);
      toast.success("Marked delivered");
      load();
    } catch { toast.error("Could not update"); }
  };

  if (orders === null) return <div className="pt-32 text-center text-zinc-500" data-testid="seller-orders-loading">Loading…</div>;

  return (
    <div data-testid="seller-orders-page" className="pt-28 pb-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Link to="/seller" className="text-xs uppercase tracking-widest text-zinc-500 hover:text-white inline-flex items-center gap-2 mb-6"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
      <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">Seller</p>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2 mb-10">Orders to fulfill</h1>

      {orders.length === 0 ? (
        <div className="card-surface p-12 text-center" data-testid="seller-orders-empty">
          <Package className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-400">No orders yet — sales will land here.</p>
        </div>
      ) : (
        <ul className="space-y-4">
          {orders.map((o) => (
            <li key={o.id} data-testid={`seller-order-${o.id}`} className="card-surface p-6">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">Order</p>
                  <p className="font-mono text-xs text-zinc-200 mt-1">#{o.id.slice(0, 8).toUpperCase()}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">Placed</p>
                  <p className="text-sm text-zinc-300 mt-1">{new Date(o.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">Subtotal (your items)</p>
                  <p className="font-display font-bold text-xl text-[#FF007F] mt-1">${Number(o.my_subtotal).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">Status</p>
                  <div className="mt-1"><StatusPill status={o.status} /></div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Items</p>
                  <ul className="divide-y divide-white/5 border-t border-white/5">
                    {o.my_items.map((it, i) => (
                      <li key={i} className="flex items-center justify-between py-2 text-sm">
                        <Link to={`/product/${it.product_id}`} className="hover:text-[#FF007F]">{it.title} <span className="text-zinc-500 text-xs">× {it.qty}</span></Link>
                        <span className="text-zinc-400">${(Number(it.price) * Number(it.qty)).toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Ship to</p>
                  {o.shipping_address ? (
                    <address className="not-italic text-xs leading-relaxed text-zinc-300">
                      <p className="font-bold text-white">{o.shipping_address.name}</p>
                      <p>{o.shipping_address.line1}{o.shipping_address.line2 ? `, ${o.shipping_address.line2}` : ""}</p>
                      <p>{o.shipping_address.city}{o.shipping_address.region ? `, ${o.shipping_address.region}` : ""} {o.shipping_address.postal}</p>
                      <p>{o.shipping_address.country}</p>
                    </address>
                  ) : <p className="text-zinc-500 text-xs">—</p>}
                </div>
              </div>

              <div className="mt-5 pt-5 border-t border-white/5 flex flex-wrap items-center justify-between gap-3">
                {o.tracking ? (
                  <div className="text-xs text-zinc-400">
                    <span className="uppercase tracking-widest text-zinc-500">Tracking</span>
                    <span className="mx-2 text-zinc-700">·</span>
                    <span className="text-white font-bold">{o.tracking.carrier}</span>
                    <span className="mx-2 text-zinc-700">·</span>
                    <span className="font-mono">{o.tracking.number}</span>
                  </div>
                ) : <span className="text-xs text-zinc-500">No tracking yet</span>}
                <div className="flex gap-2">
                  {o.status === "paid" && <ShipForm orderId={o.id} onDone={load} />}
                  {o.status === "shipped" && (
                    <button data-testid={`deliver-${o.id}`} onClick={() => markDelivered(o.id)} className="btn-secondary !py-2 !px-3 !text-[10px] inline-flex items-center gap-1 hover:!border-[#CCFF00] hover:!text-[#CCFF00]">
                      <CheckCircle2 className="w-3 h-3" /> Mark delivered
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
